import { Component, computed, input, linkedSignal, output } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FieldTree, FormField } from '@angular/forms/signals';
import {
  DmButton,
  DmButtonUtility,
  DmCard,
  DmCardBody,
  DmFileupload,
  DmIcon,
  DmInput,
  DmLabel,
  DmTooltip,
} from '@digitale-menschen/component-library-angular';
import { dmFileUploadResultInterface } from '@digitale-menschen/component-library';
import { resolveDocumentName } from '../../functions/resolve-document-name.function';
import { LanguageTab, LanguageTabsComponent, LanguageTabState } from '../language-tabs/language-tabs.component';

/**
 * One language's stored document as the component takes it.
 *
 * Structural on purpose: every API client generates its own `UebersetzungPdf` with this
 * shape, and the component must not belong to any one of them.
 */
export interface UebersetzungPdf {
  spracheLokalisierung: string;
  dateiUrl?: string | null;
  /** The stored name of the document, shown as its link where {@link UebersetzungenPdfUploadComponent.titelNamesDocument} is set. */
  dateiName?: string | null;
}

/** One language's Titel field as the parent's form holds it. */
export interface UebersetzungTitel {
  titel: string;
}

/**
 * The Titel and the PDF of a translated entity, one language at a time.
 *
 * The tab strip doubles as a completeness report: a language's dot is green once it carries
 * everything it can — a Titel where {@link titelFields} is set, and a document once the entry is
 * saved and can take one — red while it carries only one of the two, and gray while it carries
 * nothing.
 *
 * The **Titel** is optional: pass {@link titelFields} to show it, leave it unset for a document-only
 * entity. It is bound with `[formField]` straight onto the `dm-input` host, so the control
 * round-trips the value, flips `touched` on blur, and renders the schema's `required` and its own
 * error message. It is the array field of the parent's form, indexed by the active language —
 * which is why the parent seeds every entry whether or not it carries anything.
 *
 * The **document** is not part of that form: it travels to its own endpoint through
 * {@link uploadRequested}, because it is filed under the entity's ID and there is nothing to file
 * it under until the entry exists. That split is also why an unsaved entry offers no upload.
 */
@Component({
  selector: 'lp-uebersetzungen-pdf-upload',
  imports: [
    DmButton,
    DmButtonUtility,
    DmCard,
    DmCardBody,
    DmFileupload,
    DmIcon,
    DmInput,
    DmLabel,
    DmTooltip,
    FormField,
    LanguageTabsComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './uebersetzungen-pdf-upload.component.html',
  styleUrl: './uebersetzungen-pdf-upload.component.scss',
  host: { class: 'lp-uebersetzungen-pdf-upload dm-block' },
})
export class UebersetzungenPdfUploadComponent {
  /** The parent form's array field of Titel entries — one per language, in tab order. Unset hides the Titel. */
  public readonly titelFields = input<FieldTree<UebersetzungTitel[]> | undefined>(undefined);

  /**
   * For entities whose Titel names the document rather than the entry itself — a FAQ's PDF. The
   * Titel is then shown only once the active language has a document, below it, and the document's
   * link reads the document's name — the stored {@link UebersetzungPdf.dateiName}, or the file name its
   * address ends in — instead of the generic «Aktuelles PDF öffnen».
   */
  public readonly titelNamesDocument = input<boolean>(false);

  /** The Titel's label. Unset keeps the generic «Titel». */
  public readonly titelLabel = input<string | undefined>(undefined);

  /** Explains the Titel in a help icon beside its label. Unset shows no icon. */
  public readonly titelTooltip = input<string | undefined>(undefined);

  protected readonly defaultTitelLabel = $localize`:@@common.titel:Titel`;

  /** What the stored policy says about each language: its title so far, and its document. */
  public readonly uebersetzungen = input.required<UebersetzungPdf[]>();

  /** False while the entry has never been saved, which is when no document can be filed yet. */
  public readonly canUploadPdf = input<boolean>(false);

  /** True while a document is on its way, so the controls report it. */
  public readonly isUploading = input<boolean>(false);

  /** Whether the note that the document's address stays the same after a replacement is shown. */
  public readonly showStableLinkHint = input<boolean>(true);

  /** The picked document, for the language it belongs to. */
  public readonly uploadRequested = output<{ sprache: string; file: File }>();

  /** The language whose document is to be removed. */
  public readonly removeRequested = output<string>();

  /** Whether the language tab strip is shown; hide it where the parent picks the language itself. */
  public readonly showLanguageList = input<boolean>(true);

  /**
   * The language to show, by its `spracheLokalisierung` code. Unset (or unknown) keeps the
   * language the user last picked, starting with the first.
   */
  public readonly activeLanguage = input<string | undefined>(undefined);

  /** Which language is on screen, as an index into the four entries. */
  protected readonly activeIndex = linkedSignal<{ language: string | undefined; entries: UebersetzungPdf[] }, number>({
    source: () => ({ language: this.activeLanguage(), entries: this.uebersetzungen() }),
    computation: ({ language, entries }, previous) => {
      const index = language ? entries.findIndex((entry) => entry.spracheLokalisierung === language) : -1;
      return index >= 0 ? index : Math.min(previous?.value ?? 0, Math.max(entries.length - 1, 0));
    },
  });

  /** The entry being shown — its title so far, and its document. */
  protected readonly activeEntry = computed<UebersetzungPdf | undefined>(() => this.uebersetzungen()[this.activeIndex()]);

  /** The name the active document's link reads, where the Titel names the document and a name can be found. */
  protected readonly activeDocumentName = computed<string | undefined>(() => {
    const active = this.activeEntry();
    return this.titelNamesDocument() ? resolveDocumentName(active?.dateiName, active?.dateiUrl) : undefined;
  });

  /**
   * The form field the Titel input binds to.
   *
   * Indexed off the array field rather than passed in ready-made, so switching a tab moves the
   * binding to that language's own field instead of copying values between one control.
   */
  protected readonly activeTitelField = computed(() =>
    this.titelNamesDocument() && !this.activeEntry()?.dateiUrl ? undefined : this.titelFields()?.[this.activeIndex()]?.titel,
  );

  /**
   * Whether the drop zone is the thing under the caption right now.
   *
   * It decides what the caption may be: a real `<label for>` only while there is a control to
   * point at. With a document stored, the caption sits above a read-only row instead, and a
   * `<label>` would promise an association the markup cannot keep.
   */
  protected readonly showsUploadControl = computed<boolean>(() => this.canUploadPdf() && !this.activeEntry()?.dateiUrl);

  /**
   * Each language's tab, from its Titel as the form currently holds it and its stored document.
   *
   * Read off the **form** rather than off the loaded policy, so the dot follows what is being
   * typed instead of what was last saved. A document is only expected once the entry is saved,
   * since an unsaved one cannot take any.
   */
  protected readonly languageTabs = computed<LanguageTab[]>(() => {
    const titelFields = this.titelFields();
    const expectsDocument = this.canUploadPdf();
    return this.uebersetzungen().map((entry, index) => {
      const hasDocument = !!entry.dateiUrl;
      if (!titelFields) {
        return { sprache: entry.spracheLokalisierung, state: hasDocument ? 'complete' : 'empty' };
      }
      const hasTitel = !!titelFields[index]?.titel().value()?.trim();
      const isComplete = hasTitel && (hasDocument || !expectsDocument);
      const state: LanguageTabState = isComplete ? 'complete' : hasTitel || hasDocument ? 'partial' : 'empty';
      return { sprache: entry.spracheLokalisierung, state };
    });
  });

  protected readonly pdfInputId = 'uebersetzungen-pdf-upload-pdf';
  /** What the upload accepts, matching what the API accepts. */
  protected readonly pdfMimetype = 'application/pdf';
  /** The largest document the API takes, in bytes. */
  protected readonly maxPdfBytes = 20 * 1024 * 1024;
  protected readonly uploadCtaLabel = $localize`:@@common.uebersetzungenPdfUpload.upload:PDF auswählen`;
  protected readonly dragDropLabel = $localize`:@@common.uebersetzungenPdfUpload.dragDrop:oder hierher ziehen`;
  protected readonly pdfHint = $localize`:@@common.uebersetzungenPdfUpload.hint:Nur PDF, maximal 20 MB.`;
  protected readonly deleteLabel = $localize`:@@common.delete:Löschen`;

  /**
   * Hands the picked document to the drawer, which owns the request.
   *
   * `outputformat="file"` is what makes `data` a `File`; the guard is there because the type
   * allows an `ArrayBuffer` or a data URL under the other two formats.
   *
   * @param {dmFileUploadResultInterface} result - What the control picked up
   */
  protected filePicked(result: dmFileUploadResultInterface): void {
    const active = this.activeEntry();
    if (active && result.data instanceof File) {
      this.uploadRequested.emit({ sprache: active.spracheLokalisierung, file: result.data });
    }
  }

  /** Asks the drawer to remove the active language's document. */
  protected removePdf(): void {
    const active = this.activeEntry();
    if (active) {
      this.removeRequested.emit(active.spracheLokalisierung);
    }
  }
}
